import Image from "next/image";
import React from "react";
import { useOdooLiveChatLeaveMessage } from "hooks/use-odoo-live-chat-leave-message/use-odoo-live-chat-leave-message";
import CocoCustomerService from "public/assets/coco-customer-service.svg";

import {
  Bubble,
  ClosedBody,
  ClosedFooter,
  ClosedNotice,
  ComposeError,
  ComposeInput,
  ComposeRow,
  LeaveMessageButton,
  Panel,
  PanelAvatar,
  PanelClose,
  PanelDot,
  PanelHeader,
  PanelTitle,
  SendButton
} from "./odoo-live-chat-launcher.styles";

// "Tinggalkan pesan" is hidden until the feature is finished (the Odoo side exists).
const IS_LEAVE_MESSAGE_ENABLED = false;
const MAX_LENGTH = 1000;
const SENT_TEXT = "Pesanmu udah masuk, ya. Kakak Siaga bales pas buka lagi.";

// "**bold**" in the configured messages.
const renderBold = (text: string) =>
  text
    .split(/\*\*(.+?)\*\*/g)
    .map((part, index) =>
      index % 2 === 1 ? <strong key={index}>{part}</strong> : part
    );

interface IClosedPanelProps {
  messages: string[];
  canLeaveMessage: boolean;
  onClose: () => void;
}

// Shown outside the working hours: the messages configured in Odoo as bubbles,
// and optionally a way to leave a message for the operators.
export const OdooLiveChatClosedPanel: React.FC<IClosedPanelProps> = ({
  messages,
  canLeaveMessage,
  onClose
}) => {
  const { mode, setMode, text, setText, isSending, error, personalize, send } =
    useOdooLiveChatLeaveMessage();

  return (
    <Panel role="dialog" aria-label="Kakak Siaga">
      <PanelHeader>
        <PanelAvatar>
          <Image src={CocoCustomerService} alt="" fill />
          <PanelDot $isOnline={false} />
        </PanelAvatar>
        <PanelTitle>
          <strong>Kakak Siaga</strong>
          <span>Lagi offline</span>
        </PanelTitle>
        <PanelClose type="button" aria-label="Tutup" onClick={onClose}>
          ×
        </PanelClose>
      </PanelHeader>
      <ClosedBody role="status">
        {messages.map((message) => (
          <Bubble key={message}>{renderBold(personalize(message))}</Bubble>
        ))}
        {mode === "sent" && <Bubble>{SENT_TEXT}</Bubble>}
      </ClosedBody>
      <ClosedFooter>
        {mode === "compose" ? (
          <>
            <ComposeRow>
              <ComposeInput
                autoFocus
                rows={1}
                maxLength={MAX_LENGTH}
                placeholder="Tulis pesanmu di sini"
                value={text}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setText(event.target.value)
                }
              />
              <SendButton
                type="button"
                aria-label="Kirim pesan"
                disabled={!text.trim() || isSending}
                onClick={send}
              >
                →
              </SendButton>
            </ComposeRow>
            {error && <ComposeError role="alert">{error}</ComposeError>}
          </>
        ) : (
          <>
            <ClosedNotice>🕒 Chat lagi tutup sekarang</ClosedNotice>
            {IS_LEAVE_MESSAGE_ENABLED && canLeaveMessage && mode === "info" && (
              <LeaveMessageButton
                type="button"
                onClick={
                  /* istanbul ignore next -- gated behind IS_LEAVE_MESSAGE_ENABLED feature flag (false) */
                  () => setMode("compose")
                }
              >
                Tinggalkan pesan
              </LeaveMessageButton>
            )}
          </>
        )}
      </ClosedFooter>
    </Panel>
  );
};
